import { useContext, useEffect, useMemo, useState } from 'react'

import { Heading, useHealthToolsCache } from '@hhgtech/hhg-components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { QUERY_HEALTH_TOOL } from '@hhgtech/hhg-components/constants'
import { theme } from '@hhgtech/hhg-components/misc'
// import { LOCALE } from '@hhgtech/hhg-components/types'
import { yupResolver } from '@mantine/form'
import cookieParser from 'cookie'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import queryString from 'query-string'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import {
  formatOvulationSuggestedArticle,
  formatTogetherCommunity,
} from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { callApi } from 'api/utils'
import { getApiPath, getHelloSitesUrl } from 'api/utils/paths'
import { AdBlock } from 'components/molecules/adBlock'
import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/deferFixedBottomAdBlock'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import HealthToolButtonGroup from 'components/molecules/healthToolButtonGroup'
import { AdditionalInfo } from 'components/molecules/healthTools/additionalInfo'
import { ToolsBannerCardV2 } from 'components/molecules/toolsBannerCardV2'
import { ToolsSuggestionArticles } from 'components/molecules/toolsSuggestionArticles'
import { EmbeddedCommunity } from 'components/organisms/embeddedCommunity'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { OutOfPageAd } from 'components/pages/article/components/OutOfPageAd'
import {
  StyledArticleSeeMore,
  StyledSuggestedArticlesContainer,
} from 'components/pages/healthTools/index.styled'
import { HealthToolLayout } from 'components/templates/healthToolLayout'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { OvulationCalculatorType } from 'components/templates/ovulationCalculator'
import { OvulationInfo } from 'components/templates/ovulationCalculator/ovulationInfo'
import {
  createOvulationEvents,
  getFertileDates,
  getOvulationDate,
  getPeriodEndDate,
} from 'components/templates/ovulationCalculator/utils'
import useFetch from 'hooks/useFetch'
import { useIsMobile } from 'hooks/useIsMobile'
import { useMyHealth } from 'hooks/useMyHealth'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  API_DATE_FORMAT,
  DAY_JS_DATE_FORMAT,
  DISPLAY_DATE_FORMAT,
  LOCALE_SPECS,
  MY_HEALTH_ROUTES,
  SSO_URL,
  assetImagePath,
  basePath,
} from 'interfaces/constants'
import { INSKIN_OVULATION } from 'interfaces/constants/cookies'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import {
  FooterInfo,
  OvulationInfo as OvulationPageInfoType,
  TogetherCommunity,
} from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { useModalStore } from 'state/modal'
import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'
import { sendCompleteFlowEvent } from 'utils/fpixel'
import { checkAndSaveUserInfo } from 'utils/healthTools'

import { OvulationContent } from './content'
import { OvulationFormProvider, useOvulationForm } from './context'
import { generateSchema } from './helpers'
import { StyledOvulation } from './index.styled'
import { OnboardingFlow } from '../onboarding/onboardingFlow'

export type Props = {
  isMobileSsr: boolean
  ovulationPageInfo: OvulationPageInfoType
  pageType?: string
  footerInfo?: FooterInfo
  dataNavMenuContent?: NavMenuContentType
}

export const OvulationPageComponent = ({
  ovulationPageInfo,
  footerInfo,
  dataNavMenuContent,
}: Props) => {
  const { formatMessage: f } = useIntl()

  const isMobile = useIsMobile()
  const { locale, asPath, query } = useRouterExtend()
  const setShouldOnboardingModalOpen = useModalStore(
    (state) => state.setShouldOnboardingModalOpen,
  )

  const { reloadMyHealthData } = useMyHealth({ initLoad: true })
  const {
    state: { userInfo, isLoggedIn },
  } = useContext(AppContext)
  const { getHealthToolLink } = useHealthToolsCache(locale)

  const method = query[QUERY_HEALTH_TOOL.method]
  const day = query[QUERY_HEALTH_TOOL.day] as string
  const cycle = query[QUERY_HEALTH_TOOL.cycle]
  const period = query[QUERY_HEALTH_TOOL.period]
  const access_token = query[QUERY_HEALTH_TOOL.access_token]

  const shareUrl = useMemo(() => {
    return queryString.stringifyUrl({
      url: `${getHelloSitesUrl(locale)}${getHealthToolLink(
        HEALTH_TOOL_TYPE.DUE_DATE,
      )}`,
      query: {
        [QUERY_HEALTH_TOOL.method]: method,
        [QUERY_HEALTH_TOOL.day]: day,
        [QUERY_HEALTH_TOOL.cycle]: cycle,
        [QUERY_HEALTH_TOOL.period]: period,
      },
    })
  }, [getHealthToolLink])

  const { response: suggestedArticlesRes } = useFetch({
    url: getApiPath(
      PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_OVULATION_SUGGESTED_ARTICLES,
      {
        method: 1,
      },
    ),
    method: 'GET',
  })

  const ovulationSuggestedArticle = formatOvulationSuggestedArticle(
    suggestedArticlesRes?.data?.data,
  )
  const { suggestedArticles, suggestedArticlesLink } = ovulationSuggestedArticle
  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, asPath, [
      'health-tools/ovulation',
    ]),
  })
  const schema = generateSchema()

  const form = useOvulationForm({
    initialValues: {
      type: OvulationCalculatorType.TrackPeriod,
      cycle: 28,
      startDate: new Date(),
      periodLength: 7,
      isSubmitted: false,
      isYourself: true,
    },
    validate: yupResolver(schema),
  })

  useEffect(() => {
    // Set value from query params
    if (method || day) {
      form.setValues({
        type: Number(Array.isArray(method) ? method[0] : method),
        startDate: dayjs(
          Array.isArray(day) ? day[0] : day,
          DAY_JS_DATE_FORMAT,
        ).toDate(),
        cycle: cycle ? Number(Array.isArray(cycle) ? cycle[0] : cycle) : 28,
        periodLength: period
          ? Number(Array.isArray(period) ? period[0] : period)
          : 7,
        isSubmitted: true,
      })
    }
  }, [query])

  useEffect(() => {
    if (userInfo !== undefined) {
      if (!cycle || !day || !method || !period) {
        form.reset()
      } else if (access_token) {
        callApi(
          createOvulationEvents({
            method: Number(method),
            token: String(access_token),
            cycle: Number(cycle),
            day: String(day),
            user_id: String(userInfo?.id),
            period: Number(period),
          }),
          'GET',
        )
      }
    }
  }, [userInfo, query])

  useEffect(() => {
    if (userInfo?.id) {
      form.setFieldValue('isYourself', true)
      userInfo?.birthday &&
        form.setFieldValue('birthday', dayjs(userInfo.birthday).toDate())
    }
  }, [userInfo?.id])

  useEffect(() => {
    sendCompleteFlowEvent()

    const parsedCookie = cookieParser.parse(document.cookie || '')

    if (userInfo !== undefined && form.values?.isSubmitted) {
      const localData = JSON.parse(
        localStorage.getItem('ovulationInfo') || '{}',
      )
      const isExisLocalData = Object.keys(localData).length > 0
      const nextPeriodDate = dayjs(form.values.startDate)
        .add(form.values.cycle, 'day')
        .toDate()
      const ovulationDate = getOvulationDate(nextPeriodDate)
      const { fertileDateStart, fertileDateEnd } = getFertileDates(
        ovulationDate,
      )
      const periodEndDate = getPeriodEndDate(
        nextPeriodDate,
        form.values.periodLength,
      )

      Cookies.set(INSKIN_OVULATION, {
        fertile_from_date: dayjs(fertileDateStart).format(API_DATE_FORMAT),
        fertile_to_date: dayjs(fertileDateEnd).format(API_DATE_FORMAT),
        ovulation_date: dayjs(ovulationDate).format(API_DATE_FORMAT),
        menstrual_from_date: dayjs(nextPeriodDate).format(API_DATE_FORMAT),
        menstrual_to_date: dayjs(periodEndDate).format(API_DATE_FORMAT),
        cycle_length_days: form.values.cycle,
        period_duration_days: form.values.periodLength,
      })
      const data = {
        account_id: userInfo?.id || null,
        site_id: Number(LOCALE_SPECS[locale].SITE_ID),
        ga_client_id: parsedCookie['_ga'] || '',
        email: userInfo?.email || null,
        objective: form.values?.type + 1 || 0,
        first_day: dayjs(form.values.startDate).format(API_DATE_FORMAT),
        cycle_length_days: form.values.cycle,
        period_duration_days: form.values.periodLength,
        fertile_from_date: dayjs(fertileDateStart).format(API_DATE_FORMAT),
        fertile_to_date: dayjs(fertileDateEnd).format(API_DATE_FORMAT),
        ovulation_date: dayjs(ovulationDate).format(API_DATE_FORMAT),
        menstrual_from_date: dayjs(nextPeriodDate).format(API_DATE_FORMAT),
        menstrual_to_date: dayjs(periodEndDate).format(API_DATE_FORMAT),
        url: window.location.href,
        position_type: 'homepage',
        device_type: 'web',
        categories: [],
        is_my_self: Number(Boolean(localData.isYourself)),
        ...(localData.birthday && {
          birthday: dayjs(localData.birthday).format(DISPLAY_DATE_FORMAT),
        }),
        ...((localData.additionalQuestions || []).length > 0 && {
          additional_questions: localData.additionalQuestions,
        }),
        source_url: localData.source_url,
      }

      ;(async () => {
        await Promise.all([
          isExisLocalData &&
            callApiNew(
              PATHS.SIDIS.POST_SIDIS_OVULATION,
              {
                method: 'POST',
                data,
              },
              'sidis_update',
            ),
          userInfo?.id &&
            localData?.isYourself &&
            localData?.birthday &&
            checkAndSaveUserInfo({
              userInfo,
              data: {
                birthday: localData.birthday,
              },
              forceSaveBirthday: !localData?.isSubmitted,
            }),
        ])
        reloadMyHealthData()
      })()

      localStorage.removeItem('ovulationInfo')
    }
  }, [userInfo, form.values.isSubmitted])

  useEffect(() => {
    form.values.isSubmitted && setShouldOnboardingModalOpen(true)
  }, [ovulationPageInfo?.isLeadgen, form.values.isSubmitted])

  const [community, setCommunity] = useState<TogetherCommunity>()
  useEffect(() => {
    ;(async () => {
      if (isLoggedIn && suggestedArticlesRes?.data?.data?.community) {
        const response = await callApiNew(
          PATHS.TOGETHER.GET_TOGETHER_COMMUNITY_DETAIL,
          {
            params: {
              communityId:
                suggestedArticlesRes?.data?.data?.community?.community || -1,
              showHidden: 1,
            },
          },
          'together',
        )
        const totalCommunity = formatTogetherCommunity(response?.data)
        setCommunity(totalCommunity)
      }
    })()
  }, [suggestedArticlesRes?.data?.data?.community, isLoggedIn])
  useEffect(() => {
    ;(async () => {
      if (isLoggedIn && community && !community.joined) {
        await callApiNew(
          PATHS.TOGETHER.POST_TOGETHER_JOIN_COMMUNITY,
          {
            method: 'POST',
            data: {
              ids: [community?.id],
            },
          },
          'together',
        )
        setCommunity((p) => ({ ...p, joined: true } as TogetherCommunity))
      }
    })()
  }, [isLoggedIn, community?.joined])

  const userProfileUrl = `${SSO_URL}user`

  const recordPageItemSubmitted = [
    {
      label: f({
        id: 'healthTool.breadcrumb.myHealth',
        defaultMessage: 'My Health',
      }),
      value: `${userProfileUrl}/health`,
    },
    {
      label: f({
        id: 'ovulation.breadcrumb.result',
        defaultMessage: 'Ovulation Result',
      }),
      active: true,
      value: '',
    },
  ]
  const pageItemSubmitted = [
    {
      label: f({ id: 'healthtools.title' }),
      value: `/health-tools`,
    },
    {
      label: f({ id: 'ovulationPage.seoTitle' }),
      value: `${getHelloSitesUrl(locale)}${basePath}${getHealthToolLink(
        HEALTH_TOOL_TYPE.OVULATION,
      )}`,
    },
    {
      label: f({
        id: 'ovulation.breadcrumb.result',
        defaultMessage: 'Ovulation Result',
      }),
      value: '',
    },
  ]
  const pageItemDefault = [
    {
      label: f({ id: 'healthtools.title' }),
      value: `/health-tools`,
    },
    {
      label: f({ id: 'ovulationPage.seoTitle' }),
      value: '#',
      dataEventCategory: 'Breadcrumb',
    },
  ]

  const pageItem = form.values?.isSubmitted
    ? query[QUERY_HEALTH_TOOL.source] === 'my-health'
      ? recordPageItemSubmitted
      : pageItemSubmitted
    : pageItemDefault

  const hasResult = !!form.values?.isSubmitted
  // const showAd = locale === LOCALE.Indonesia || hasResult
  const enabledAd = !!ovulationPageInfo?.enable_ad

  return (
    <>
      <NavigatorLayout
        title={f({ id: 'ovulationPage.seoTitle' })}
        description={f({ id: 'ovulationPage.seoDescription' })}
        image={`${getHelloSitesUrl(
          locale,
        )}${assetImagePath}/ovulation-result-og-default.png`}
        SEOData={{
          schemaData: f({ id: 'ovulationPage.seoSchema' }),
          apiMetaData: ovulationPageInfo?.metaTag,
          isIndexable: true,
        }}
        footerInfo={footerInfo}
        isSponsored={ovulationPageInfo?.is_sponsored}
        sponsoredBy={ovulationPageInfo?.sponsor}
        sponsoredType="content"
        dataNavMenuContent={dataNavMenuContent}
        activeMenuService="tools"
      >
        <OvulationFormProvider form={form}>
          <StyledOvulation>
            {!form.values.isSubmitted ? (
              <ToolsBannerCardV2
                breadcrumbProps={{
                  pageItem,
                }}
                icon={
                  <img
                    className="health-tool-icon"
                    alt="health-tool-icon"
                    loading="lazy"
                    src={
                      ovulationPageInfo.featuredImage ||
                      `${assetImagePath}/ovulation-icon.svg`
                    }
                  />
                }
                heading={
                  ovulationPageInfo?.title ||
                  f({ id: 'healthTools.ovulationCalculator' })
                }
                description={
                  ovulationPageInfo?.description ||
                  f({ id: 'ovulationPage.description' })
                }
                reviewBy={ovulationPageInfo?.reviewBy}
                isMobile={isMobile}
                isPink
              />
            ) : !isMobile ? (
              <HealthToolBreadcrumb pageItem={pageItem} />
            ) : null}
            <HealthToolLayout>
              <HealthToolLayout.LeftColumn
                style={form.values.isSubmitted ? { padding: 0 } : undefined}
              >
                <OvulationContent
                  enabledAd={enabledAd}
                  ovulationPageInfo={ovulationPageInfo}
                  adUnitPath={isMobile ? adUnitPath : ''}
                />
                {form.values.isSubmitted && (
                  <>
                    <Heading
                      tag="h1"
                      style={{
                        width: 0,
                        overflow: 'hidden',
                        position: 'absolute',
                        zIndex: -1,
                      }}
                    >
                      {pageItem[pageItem.length - 1].label}
                    </Heading>
                    <AdditionalInfo
                      heading={ovulationPageInfo.additionalInfo.title}
                      content={ovulationPageInfo.additionalInfo.description}
                      ctaUrl={ovulationPageInfo.additionalInfo.ctaUrl}
                      ctaTitle={ovulationPageInfo.additionalInfo.ctaTitle}
                      image={ovulationPageInfo.additionalInfo.image}
                    />
                    <div style={{ marginBottom: '64px' }}>
                      <HealthToolButtonGroup
                        toolLink={shareUrl}
                        onRecalculate={() =>
                          (window.location.href =
                            getHealthToolLink(HEALTH_TOOL_TYPE.OVULATION) ||
                            '/')
                        }
                        saveLabel={f({
                          id: 'pregnancyTool.save',
                          defaultMessage: 'Go to My Health',
                        })}
                        onSave={() =>
                          (window.location.href = `${SSO_URL}${MY_HEALTH_ROUTES.MY_HEALTH_TRACKER}`)
                        }
                        tracking={{
                          share: {
                            'data-event-action': 'My Health Click',
                            'data-event-category': 'Health Tool',
                            'data-event-label': `${SSO_URL}${MY_HEALTH_ROUTES.MY_HEALTH_TRACKER}`,
                          },
                          save: {
                            'data-event-action': 'My Health Click',
                            'data-event-category': 'Health Tool',
                            'data-event-label': `${SSO_URL}${MY_HEALTH_ROUTES.MY_HEALTH_TRACKER}`,
                          },
                          reCalculate: {
                            'data-event-action': 'My Health Click',
                            'data-event-category': 'Health Tool',
                            'data-event-label': `${getHelloSitesUrl(
                              locale,
                            )}${getHealthToolLink(HEALTH_TOOL_TYPE.OVULATION)}`,
                          },
                        }}
                      />
                    </div>

                    {suggestedArticlesRes?.data?.data?.community && (
                      <EmbeddedCommunity.HealthTool
                        community={suggestedArticlesRes?.data?.data?.community}
                      />
                    )}

                    {ovulationPageInfo?.disclaimer && (
                      <OvulationInfo
                        heading={f({ id: 'ovulationPage.disclaimer' })}
                        content={ovulationPageInfo.disclaimer}
                      />
                    )}
                    {ovulationPageInfo?.info && (
                      <OvulationInfo
                        heading={f({ id: 'ovulationPage.faqs' })}
                        infos={ovulationPageInfo.info || []}
                      />
                    )}
                  </>
                )}
                {isMobile && (
                  <HealthToolLayout.RightColumn>
                    {hasResult &&
                      suggestedArticles &&
                      suggestedArticles.length > 0 && (
                        <>
                          <Heading
                            tag="h3"
                            color={theme.colors.gray800}
                            style={{ width: '100%', padding: '8px 16px' }}
                          >
                            {f({
                              id: 'tools.suggestedArticles',
                              defaultMessage: 'Suggested articles',
                            })}
                          </Heading>
                          <ToolsSuggestionArticles
                            isMobile
                            expertsArticles={suggestedArticles}
                            isLoading={false}
                            hideUIElements
                          />
                          <StyledArticleSeeMore href={suggestedArticlesLink}>
                            <Heading
                              tag="h5"
                              color={theme.colors.primaryBase}
                              className="see-more-text"
                            >
                              {f({
                                id: 'home.seeMore',
                                defaultMessage: 'See More',
                              })}
                            </Heading>
                            <img
                              src={`${assetImagePath}/chevronRightBlue.svg`}
                              style={{ width: '16px', height: '16px' }}
                              alt={f({
                                id: 'home.seeMore',
                                defaultMessage: 'See More',
                              })}
                              loading="lazy"
                            />
                          </StyledArticleSeeMore>
                        </>
                      )}
                    <div className="top-ad"></div>
                  </HealthToolLayout.RightColumn>
                )}
              </HealthToolLayout.LeftColumn>
              {!isMobile && (
                <HealthToolLayout.RightColumn
                  style={form.values.isSubmitted ? { padding: 0 } : undefined}
                >
                  <div className="top-ad">
                    {enabledAd && (
                      <AdBlock
                        adUnitPath={adUnitPath}
                        size={[
                          [300, 250],
                          [300, 600],
                        ]}
                        divId={'div-gpt-ad-topbanner-desktop'}
                        pos={['TopBanner']}
                      />
                    )}
                  </div>
                  {hasResult &&
                    suggestedArticles &&
                    suggestedArticles.length > 0 && (
                      <StyledSuggestedArticlesContainer>
                        <Heading
                          tag="h3"
                          color={theme.colors.gray800}
                          style={{ width: '100%', padding: '8px 16px' }}
                        >
                          {f({
                            id: 'tools.suggestedArticles',
                            defaultMessage: 'Suggested articles',
                          })}
                        </Heading>
                        <ToolsSuggestionArticles
                          isMobile
                          expertsArticles={suggestedArticles}
                          isLoading={false}
                          hideUIElements
                        />
                        <StyledArticleSeeMore href={suggestedArticlesLink}>
                          <Heading
                            tag="h5"
                            color={theme.colors.primaryBase}
                            className="see-more-text"
                          >
                            {f({
                              id: 'home.seeMore',
                              defaultMessage: 'See More',
                            })}
                          </Heading>
                          <img
                            src={`${assetImagePath}/chevronRightBlue.svg`}
                            style={{ width: '16px', height: '16px' }}
                            alt={f({
                              id: 'home.seeMore',
                              defaultMessage: 'See More',
                            })}
                            loading="lazy"
                          />
                        </StyledArticleSeeMore>
                      </StyledSuggestedArticlesContainer>
                    )}
                  <div className="bottom-ad"></div>
                </HealthToolLayout.RightColumn>
              )}
            </HealthToolLayout>
            {hasResult && (
              <OnboardingFlow
                campaign={ovulationPageInfo.collectingData.campaigns}
                isSkippable={ovulationPageInfo?.isSkippable}
                typeForm={ovulationPageInfo?.collectingTypes}
                phoneVerify={ovulationPageInfo?.phoneVerification}
                toolName="ovulation"
              />
            )}
            {enabledAd && (
              <AdBlockContainer>
                <FixedBottomAdBlock
                  className="article-bottom-ad"
                  isMobile={isMobile}
                  adUnitPath={adUnitPath}
                />
                <OutOfPageAd
                  id={`div-gpt-out-of-page`}
                  adUnitPath={adUnitPath}
                />
              </AdBlockContainer>
            )}
          </StyledOvulation>
          {isMobile && <HealthToolBreadcrumb pageItem={pageItem} />}
        </OvulationFormProvider>
      </NavigatorLayout>
      <MobileBottomNavigationWrapper.FitnessHealthTool shareUrl={shareUrl} />
    </>
  )
}
